import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { DonutChartComponent, DonutChartData } from '../../../../../_shared/components/graphs/donut-chart/donut-chart.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { FilterService } from '../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { DashboardService } from '../../../services/dashboard.service';
import { WidgetFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { ModalService } from '../../../../../_shared/services/modals/modal.service';
import { PartsInventoryModalComponent } from '../../modals/parts-inventory-modal/parts-inventory-modal.component';
import { PartsGraphRequest } from '../../../models/dashboard.model';
import { getCategoryColor, getStockStatusColor } from '../../../helpers/parts-graph.helper';
import { DASHBOARD_LABEL_KEYS } from '../../../helpers/dashboard-label-keys.helper';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-parts-inventory-trend',
  standalone: true,
  imports: [CommonModule, NgbDropdownModule, DonutChartComponent, EmptyStateComponent, TranslocoPipe],
  templateUrl: './parts-inventory-trend.component.html',
  styleUrl: './parts-inventory-trend.component.scss'
})
export class PartsInventoryTrendComponent implements OnInit, OnDestroy {
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private filterService = inject(FilterService);
  private dashboardService = inject(DashboardService);
  private errorHandlerService = inject(ErrorHandlerService);
  private modalService = inject(ModalService);

  protected data: DonutChartData[] = [];
  protected chartData: DonutChartData[] = [];

  title: string = 'maintenanceDashboard.charts.partsAndInventory';
  isLoading: boolean = true;
  hoveredPercentage: number | null = null;
  hoveredSegmentName: string | null = null;
  widgetFilter!: WidgetFilterData;

  filterOptions: string[] = ['All', 'Filters'];
  selectedFilter: string = 'All';
  private isShowingCategoryData: boolean = false;

  // Display keys only - the option itself stays the stable value sent to the service.
  readonly filterOptionLabelKeys: Record<string, string> = {
    'All': 'common.all',
    'Filters': 'maintenanceDashboard.partsInventory.categoryFilters'
  };

  private cancelSubject: Subject<void> = new Subject<void>();

  constructor() {
    this.filterService.filterUpdated$.subscribe(async (filter) => {
      if (Object.values(filter).every(x => x === null) || filter === null) return;

      this.widgetFilter = filter;
      await this.loadPartsInventoryData();
    });
  }

  ngOnInit(): void {
    this.widgetFilter = this.filterService.filter;

    if (this.widgetFilter && !Object.values(this.widgetFilter).every(x => x === null)) {
      this.loadPartsInventoryData();
    }
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private async loadPartsInventoryData(): Promise<void> {
    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: PartsGraphRequest = {
        accountIds: this.widgetFilter.accountIds,
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval
      };

      const response = this.isCategoryView
        ? await this.dashboardService.getPartsByCategoryGraph(request, this.cancelSubject)
        : await this.dashboardService.getPartsStockStatusGraph(request, this.cancelSubject);

      this.data = (response.data ?? []).map((item, index) => ({
        name: item.label,
        value: Number(item.totalValue) || 0,
        color: this.isCategoryView ? getCategoryColor(index) : getStockStatusColor(item.label, index)
      }));
      this.chartData = this.data.filter(item => item.value > 0);
      this.isShowingCategoryData = this.isCategoryView;
      this.hoveredPercentage = null;
      this.hoveredSegmentName = null;

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.data = [];
          this.chartData = [];
          this.isLoading = false;
        }
      });
    }
  }

  onFilterChange(filter: string): void {
    this.selectedFilter = filter;
    this.loadPartsInventoryData();
  }

  private get isCategoryView(): boolean {
    return this.selectedFilter === 'Filters';
  }

  get centerLabelKey(): string {
    return this.isShowingCategoryData ? 'maintenanceDashboard.score' : 'maintenanceDashboard.stockLevels';
  }

  hasData(): boolean {
    if (!this.data || this.data.length === 0) return false;

    return this.data.some(item => {
      const value = Number(item.value) || 0;
      return value > 0;
    });
  }

  get soleSegmentName(): string | null {
    return this.chartData.length === 1 ? this.chartData[0].name : null;
  }

  get legendItems() {
    return this.data.map(item => ({
      label: item.name,
      labelKey: DASHBOARD_LABEL_KEYS[item.name] as string | undefined,
      color: item.color,
      percentage: ((item.value / this.data.reduce((sum, d) => sum + d.value, 0)) * 100).toFixed(0)
    }));
  }

  onSegmentHover(event: { percentage: number; name: string } | null): void {
    if (event) {
      this.hoveredPercentage = event.percentage;
      this.hoveredSegmentName = event.name;
    } else {
      this.hoveredPercentage = null;
      this.hoveredSegmentName = null;
    }
  }

  getCenterPercentage(): string {
    if (this.hoveredPercentage !== null) return `${this.hoveredPercentage}%`;
    if (this.soleSegmentName !== null) return '100%';
    return this.transloco.translate('maintenanceDashboard.partsInventory.percentage');
  }

  openModal(): void {
    this.modalService.open(PartsInventoryModalComponent, {
      size: 'xl',
      centered: true
    });
  }
}